import Player from 'react-player/file';

const SRC = '_app.helper.ts';

import { FILE_QUALITY } from 'constants/params';
import { FileConfig, FileStatus, FileUrls, ProjectFile, ConfigFlags as FileConfigFlags } from 'services/file.service';
import { StoryBoardTracks, StoryBoard } from 'services/storyboard.service';
import { ClipConfig, ConfigFlags as ClipConfigFlags, DEFAULT_CLIP_CONFIG } from 'services/clip.service';
import { FinalCut, RenderStatus } from 'services/finalcut.service';
import { Project } from 'services/project.service';
import { Story } from 'services/story.service';
import { FilesStatusesMap } from 'components/app/_mediator/app-files.slice';
import { Clip, ClipsMap, ClipIdParsed } from 'components/app/editor/_mediator/editor.slice';
import { SbPosition, SB_POSITION } from 'components/app/editor/_mediator/editor-controls.slice';
import { SB_CLIP_PLAYBACK_PARAMS, SbClipPlaybackMap } from 'components/app/storyboard/_mediator/sb.slice';
import { FcClip } from 'components/app/finalcuts/_mediator/fc-data.slice';

export const isNull = (value: unknown | null): value is null => value === null;

export const roundTime = (time: number) => {
  return roundNumber(time, 6);
};

/**
 * Calculates the position percentage for a given time within a duration
 * @param time - Time in seconds
 * @param duration - Total duration in seconds
 * @returns Position as a percentage string (e.g., "45.5%")
 */
export const calculateTimePosition = (time: number, duration: number): string => {
  if (!duration || duration === 0) return '0%';
  return `${(time / duration) * 100}%`;
};

/**
 * Rounds a number to a given number of decimal places
 * and returns 0 if the result is very close to zero.
 *
 * @param time - The number to round
 * @param exponent - Number of decimal places (default is 6)
 * @param epsilon - Threshold for treating small values as zero (default is 1e-10).
 *                  Helps eliminate floating-point artifacts like -8.881784197001252e-16
 *                  that can appear due to precision errors in calculations.
 * @returns The rounded number, or 0 if it's within the epsilon threshold
 */
export const roundNumber = (time: number, exponent = 6, epsilon = 1e-10): number => {
  const exp = Math.pow(10, exponent);
  const rounded = Math.round((time + Number.EPSILON) * exp) / exp;
  return Math.abs(rounded) < epsilon ? 0 : rounded;
};

export const convertTransition = (storyTransitionTime: number) => {
  return storyTransitionTime / 1000;
};

export const prettyBytes = (bytes: number | null, decimals = 2) => {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  for (i; bytes > 1024; i++) {
    bytes /= 1024;
  }
  return parseFloat(bytes.toFixed(decimals)) + ' ' + units[i];
};

export const withSpaces = (string: string | undefined) => {
  if (!string) return '';
  return string.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export const prettyLabel = (string: string | undefined) => {
  if (!string) return '';
  const withSpacesResult = withSpaces(string);
  return withSpacesResult
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const prettyDate = (data: string | null) => {
  return data ? new Date(data).toLocaleString('en-US') : '';
};

export const parseBytes = (input: string) => {
  const units: Record<string, number> = {
    B: 1,
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
    TB: 1024 * 1024 * 1024 * 1024,
  };
  const parts = input.trim().split(' ');

  if (parts.length !== 2) return 0;

  const value = parseFloat(parts[0]);
  const unit = parts[1].toUpperCase();

  if (isNaN(value) || !(unit in units)) return 0;

  return value * units[unit];
};

export const prettyDuration = (ms: number | null, withMilliseconds?: boolean) => {
  if (!ms) return ``;
  ms *= 1000;
  const milliseconds = Math.floor(ms) % 1000;
  const seconds = Math.floor(ms / 1000) % 60;
  const minutes = Math.floor(ms / (1000 * 60)) % 60;
  const hours = Math.floor(ms / (1000 * 60 * 60));

  const padPeriod = (period: number, length: number) => period.toString().padStart(length, '0');
  const millisecondsValue = withMilliseconds ? `.${padPeriod(milliseconds, 3)}` : '';
  const hoursValue = hours ? `${padPeriod(hours, 2)}:` : '';

  return `${hoursValue}${padPeriod(minutes, 2)}:${padPeriod(seconds, 2)}${millisecondsValue}`;
};

export const createSetFromArray = <T>(array: Array<T>) => {
  const res: Set<T> = new Set();
  array.forEach((val) => res.add(val));
  return res;
};

export const getInternalPlayer = (player?: Player | null) => {
  return player?.getInternalPlayer() as HTMLVideoElement | undefined;
};

export const getRandomizedArray = <T>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

export const prettyResolution = (width: number | null, height: number | null) => {
  if (height && width) return `${width}x${height}`;
};

export const prettyBitrate = (oldBitRate: number | null, newBitRate: number | null) => {
  if (oldBitRate && newBitRate) {
    return `${oldBitRate} => ${newBitRate} Kb/s`;
  } else if (oldBitRate) {
    return `${oldBitRate} Kb/s`;
  }
};

export const prettyFrameRate = (frameRate: number | null) => {
  if (frameRate && frameRate > 0) {
    if (frameRate % 1 === 0) {
      return `${Math.round(frameRate)} fps`;
    } else {
      return `${frameRate.toFixed(2)} fps`;
    }
  }
};

// P R O J E C T

export const sortByDate = <T extends { createdAt: string; accessedAt: string | null }>(
  elements: T[],
  sortBy: 'accessed' | 'created'
): T[] => {
  if (sortBy === 'accessed')
    return elements
      .filter((el) => !isNull(el.accessedAt))
      .sort((a, b) => new Date(b.accessedAt!).getTime() - new Date(a.accessedAt!).getTime());
  else return elements.slice().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
};

export const getDefaultProject = (projects: Project[]) => {
  if (!projects.length) return null;
  const byAccessed = sortByDate(projects, 'accessed');
  if (byAccessed.length) return byAccessed[0].projectId;
  const byCreated = sortByDate(projects, 'created');
  return byCreated[0].projectId;
};

export const getDefaultStory = (stories: Story[]) => {
  if (!stories.length) return null;
  const byAccessed = sortByDate(stories, 'accessed');
  if (byAccessed.length) return byAccessed[0].storyId;
  const byCreated = sortByDate(stories, 'created');
  return byCreated[0].storyId;
};

export const getDefaultFc = (finalcuts: FinalCut[]) => {
  if (!finalcuts.length) return null;
  const byAccessed = sortByDate(finalcuts, 'accessed');
  if (byAccessed.length) return byAccessed[0].id;
  const byCreated = sortByDate(finalcuts, 'created');
  return byCreated[0].id;
};

export const getDefaultFile = (files: ProjectFile[]) => {
  if (!files.length) return null;
  const byAccessed = sortByDate(files, 'accessed');
  if (byAccessed.length) return byAccessed[0].id;
  const byCreated = sortByDate(files, 'created');
  return byCreated[0].id;
};

// U R L S

/**
 * Reads the CloudFront `Expires` (unix seconds) param off a signed URL.
 */
export const getSignedUrlExpiryMs = (url: string | null | undefined): number | null => {
  if (!url) return null;
  const match = url.match(/[?&]Expires=(\d+)/);
  return match ? Number(match[1]) * 1000 : null;
};

/**
 * Earliest expiry among a set of signed URLs, or null if none carry one.
 */
export const earliestSignedUrlExpiryMs = (urls: (string | null | undefined)[]): number | null => {
  const expiries = urls.map(getSignedUrlExpiryMs).filter((ms): ms is number => ms !== null);
  return expiries.length ? Math.min(...expiries) : null;
};

// F I L E S

type UrlKeys = {
  main: keyof FileUrls;
  reverse: keyof FileUrls;
  stabilization: keyof FileUrls;
  reverseStabilization: keyof FileUrls;
};

const urlKeys = ((): UrlKeys => {
  switch (FILE_QUALITY) {
    case 'high':
      return {
        main: 'fileUrl',
        reverse: 'reverseMediumQualityUrl',
        stabilization: 'stabilizationMediumQualityUrl',
        reverseStabilization: 'reverseWithStabilizationMediumQualityUrl',
      };
    case 'medium':
      return {
        main: 'mediumQualityUrl',
        reverse: 'reverseMediumQualityUrl',
        stabilization: 'stabilizationMediumQualityUrl',
        reverseStabilization: 'reverseWithStabilizationMediumQualityUrl',
      };
    case 'low':
      return {
        main: 'lowQualityUrl',
        reverse: 'reverseLowQualityUrl',
        stabilization: 'stabilizationLowQualityUrl',
        reverseStabilization: 'reverseWithStabilizationLowQualityUrl',
      };
    default:
      return {
        main: 'fileUrl',
        reverse: 'reverseMediumQualityUrl',
        stabilization: 'stabilizationMediumQualityUrl',
        reverseStabilization: 'reverseWithStabilizationMediumQualityUrl',
      };
  }
})();

export const getVideoUrl = (file: ProjectFile): Url => {
  const urlsMap = file.fileWithEffectsUrls;
  const reverse = file.config.flags.includes(FileConfigFlags.reverse);
  const stabilization = file.config.flags.includes(FileConfigFlags.stabilization);
  let url: Url | null;
  if (!reverse && !stabilization) url = urlsMap[urlKeys.main];
  else if (!stabilization) url = urlsMap[urlKeys.reverse] ?? urlsMap[urlKeys.main];
  else if (!reverse) url = urlsMap[urlKeys.stabilization] ?? urlsMap[urlKeys.main];
  else
    url =
      urlsMap[urlKeys.reverseStabilization] ??
      urlsMap[urlKeys.reverse] ??
      urlsMap[urlKeys.stabilization] ??
      urlsMap[urlKeys.main];
  return url ?? urlsMap.fileUrl ?? '';
};

export const isFileStatusCompleted = (status: FileStatus) => {
  return [FileStatus.Error, FileStatus.Available, FileStatus.UploadInterrupted].includes(status);
};

export const isFilesProcessing = (statuses: FilesStatusesMap) => {
  return Object.values(statuses).some((status) => !isFileStatusCompleted(status));
};

// C L I P S

export const createClipId = (clip: ClipIdParsed) => {
  return `${clip.mediaFileId}-${clip.startTime}-${clip.endTime}`;
};

export const parseClipId = (clipId: ClipId): ClipIdParsed => {
  const [fileId, startTime, endTime] = clipId.split('-') as [FileId: string, StartTime: string, EndTime: string];
  if (fileId === undefined || startTime === undefined || endTime === undefined) {
    console.error(`[${SRC}] parseClipId`);
  }
  return {
    mediaFileId: Number(fileId),
    startTime: startTime === 'null' ? null : Number(startTime),
    endTime: endTime === 'null' ? null : Number(endTime),
  };
};

export const newClip = (clipBase: ClipIdParsed): Clip => {
  return {
    clipId: createClipId(clipBase),
    clipGroupId: 0,
    config: DEFAULT_CLIP_CONFIG,
    ...parseClipId(createClipId(clipBase)),
    // Default values for backend-provided properties (will be set when synced with backend)
    isAutoHighlight: undefined,
    isPinned: undefined,
  };
};

export const selectClipData = (clipId: ClipId, clipsMap: ClipsMap) => {
  const clip = clipsMap[clipId];
  if (clip) return clip;
  console.log(`[${SRC}] selectClipData: clip not found`, { clipId });
  return newClip(parseClipId(clipId));
};

/**
 * Calculates the confidence score for a highlight clip
 * @param clipData - The clip data
 * @returns The rounded confidence score as a percentage, or null if not a highlight
 */
export const getClipConfidenceScore = (clipData: Clip | null | undefined): number | null => {
  if (!clipData) return null;
  // Check if it's a highlight - use isAutoHighlight property if available, otherwise check score
  const isHighlight = clipData.isAutoHighlight ?? (clipData.score != null && clipData.highlightId != null);
  if (!isHighlight) return null;
  return Math.round(clipData.score!);
};

/**
 * Determines if the pin button should be shown for a clip
 * @param clipData - The clip data
 * @returns true if the pin button should be shown (i.e., it's a highlight)
 */
export const shouldShowPinButton = (clipData: Clip | null | undefined): boolean => {
  if (!clipData) return false;
  // Use backend-provided isAutoHighlight property if available, otherwise fall back to checking highlightId
  return clipData.isAutoHighlight ?? (clipData.score != null && clipData.highlightId != null);
};

/**
 * Counts clips from clips table: pinned highlights + manual clips
 * Excludes unpinned highlights from the highlights table
 * @param fileClips - Array of clip IDs for a file
 * @param clipsMap - Map of all clips
 * @returns Number of clips on storyboard (pinned highlights + manual clips)
 */
export const countStoryboardClips = (
  fileClips: string[] | undefined,
  clipsMap: ClipsMap | null | undefined
): number => {
  if (!fileClips || !clipsMap) return 0;
  return fileClips.filter((clipId: string) => {
    const clip = clipsMap[clipId];
    // Include if it's manually added, or if it's a pinned highlight
    return clip && (!clip.isAutoHighlight || clip.isPinned === true);
  }).length;
};

export const getClipTime = (clip: ClipIdParsed, inverse?: 'inverse') => {
  if (inverse) return clip.endTime ?? (clip.startTime as number);
  return clip.startTime ?? (clip.endTime as number);
};

export const getClipTransition = (cellDuration: Seconds, transitionTime: Milliseconds) => {
  const transitionSeconds = convertTransition(transitionTime);
  return transitionSeconds > cellDuration ? cellDuration : transitionSeconds;
};

export const getClipLabel = (clipData: ClipIdParsed) => {
  const startTime = !isNull(clipData.startTime) ? convertClipTimeForHuman(clipData.startTime) : null;
  const endTime = !isNull(clipData.endTime) ? convertClipTimeForHuman(clipData.endTime) : null;
  if (!isNull(startTime) && !isNull(endTime)) return startTime + ' → ' + endTime;
  else if (!isNull(startTime)) return startTime + ' →';
  return ' → ' + endTime;
};

export const selectSbClipPlaybackParams = (
  clipId: ClipId,
  SbClipPlaybackMap: SbClipPlaybackMap | null,
  strict = true
) => {
  if (isNull(SbClipPlaybackMap)) {
    if (strict) console.error(`[${SRC}] selectSbClipPlaybackParams: SbClipPlaybackMap is not initialized`);
    return SB_CLIP_PLAYBACK_PARAMS;
  }
  const playbackParams = SbClipPlaybackMap[clipId];
  if (!playbackParams) {
    if (strict) console.error(`[${SRC}] selectSbClipPlaybackParams: clip not found`);
    return SB_CLIP_PLAYBACK_PARAMS;
  }
  return SbClipPlaybackMap[clipId];
};

export const convertClipTimeForHuman = (time: number): string => {
  const h = (time / 3600) | 0;
  const m = ((time % 3600) / 60) | 0;
  const s = time % 60;

  const sFixed = s.toFixed(2);
  const secStr = m > 0 && s < 10 ? sFixed.padStart(5, '0') : sFixed.padStart(4, '0'); // SS.ss

  if (h) {
    const hStr = h.toString().padStart(2, '0');
    const mStr = m.toString().padStart(2, '0');
    return `${hStr}:${mStr}:${secStr}`; // HH:MM:SS.ss
  }
  if (m >= 10) {
    const mStr = m.toString().padStart(2, '0');
    return `${mStr}:${secStr}`; // MM:SS.ss
  }
  if (m > 0) return `${m}:${secStr}`; // M:SS.ss

  return s >= 10 ? secStr : sFixed;
};

export const getClipIdFromString = (str: string) => {
  const regex = /Clip (\d+-(?:null|[\d.]+)-(?:null|[\d.]+))/;
  const match = str.match(regex);
  return match ? match[1] : null;
};

export function sortClips(clips: FcClip[]): FcClip[];
export function sortClips(clips: Clip[]): Clip[];
export function sortClips(clips: ClipId[]): ClipId[];
export function sortClips(clips: (ClipId | Clip | FcClip)[]) {
  const sortCb = (a: ClipIdParsed, b: ClipIdParsed) => {
    const _a = getClipTime(a);
    const _b = getClipTime(b);
    if (!isNull(_a) && !isNull(_b)) {
      if (_a === _b) {
        if (a.startTime) return 1;
        else return -1;
      }
      return _a - _b;
    }
    return 0;
  };
  return clips.sort((a, b) => {
    if (typeof a === 'string') {
      const clipA = parseClipId(a);
      const clipB = parseClipId(b as string);
      return sortCb(clipA, clipB);
    } else return sortCb(a, b as Clip);
  });
}

// S T O R Y B O A R D

export const getSbBlank = (): StoryBoard => {
  return {
    storyBoard: [],
    clipBoard: [],
    discardBoard: [],
  };
};

export const getSbCopy = (sb: StoryBoard): StoryBoard => {
  const res: StoryBoard = getSbBlank();
  getSbTracks().forEach((track) => (res[track] = [...sb[track]]));
  return res;
};

export const getSbTracks = (): StoryBoardTracks[] => {
  return Object.keys(StoryBoardTracks) as StoryBoardTracks[];
};

export const getSbTrackIndex = (track: StoryBoardTracks): Index => {
  return Object.keys(StoryBoardTracks).indexOf(track);
};

export const isSbCellEmpty = (storyBoard: StoryBoard, { track, cell }: SbPosition) => storyBoard[track][cell] == null;

export const findSbPosition = (payload: CellClip, storyBoard: StoryBoard, startPosition: SbPosition = SB_POSITION) => {
  const tracks = getSbTracks();
  const startTrackIndex = getSbTrackIndex(startPosition.track);
  for (let index = startTrackIndex; index < tracks.length; index++) {
    const track = tracks[index];
    const cell = storyBoard[track].findIndex(
      (id, i) => !(track === startPosition.track && i < startPosition.cell) && id === payload
    );
    if (cell !== -1) return { cell, track };
  }
};

export const getSbPosition = (payload: CellClip, storyBoard: StoryBoard, startFrom: SbPosition = SB_POSITION) => {
  const position = findSbPosition(payload, storyBoard, startFrom);
  if (position) return position;
  console.error(`[${SRC}] getSbPosition`, { payload });
  return SB_POSITION;
};

// F I N A L C U T S

export const findFcPosition = (clipId: ClipId, storyboard: CellClip[]): Index | undefined => {
  const index = storyboard.findIndex((cell) => cell === clipId);
  if (index === -1) return undefined;
  return index;
};

export const isFcStatusCompleted = (status: RenderStatus) => {
  return [RenderStatus.error, RenderStatus.rendered].includes(status);
};

// E F F E C T S

export const getFileConfigFlags = (config: FileConfig) => {
  const result = config.flags.map((flag) => FileConfigFlags[flag]).filter(Boolean);
  if (config.rotate) result.push('rotate');
  return result;
};

export const getClipConfigFlags = (config: ClipConfig) => {
  const result = config.flags.map((flag) => ClipConfigFlags[flag]).filter(Boolean);
  if (config.rotate) result.push('rotate');
  return result;
};
